import React, { useState, useEffect, useContext } from 'react';
import DisplayPicture from 'assets/images/dp.png';
import moment from 'moment';
import { UserProfileContext } from './context';
import { DateRangeSelector, ENUM_DATE_RANGE } from '@cokitchen/cokitchen-components';
import BaseStats, { BaseStatsType } from 'components/base/stats-details';
import { apiService } from 'utils/api';
import { formatNumber, getInterval, getUserName, timeConvert } from 'utils/functions';
import Loader from 'components/base/loader';

type ResponseType = {
  total_orders: string;
  basket_abandonment_rate: number;
  average_order_duration: number;
  total_amount_spent: number;
};

const UserprofileHeader = () => {
  const { user, filters, setFilters } = useContext(UserProfileContext);
  const [userSummary, setUserSummary] = useState([] as BaseStatsType[]);
  const [loading, setLoading] = useState(false);

  const getUsersStatsByDate = async () => {
    const { startDate: start_date, endDate: end_date } = filters.date;

    const params: any = {
      start_date,
      end_date
    };
    const res = await apiService.get(`cs/user/one/${user?.id}`, {
      data: params,
      baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
    });
    const data: ResponseType = res?.data?.data;

    setUserSummary([
      {
        title: 'total orders',
        value: data?.total_orders
      },
      {
        title: 'total spent',
        value: data?.total_amount_spent ? String(data?.total_amount_spent) : '-',
        withNaira: data?.total_amount_spent > 0
      },
      {
        title: 'AVG. ORDER DURATION',
        value: data?.average_order_duration ? timeConvert(data?.average_order_duration) : '-'
      },

      {
        title: 'BASKET ABANDONMENT',
        value:
          typeof data?.basket_abandonment_rate === 'number'
            ? `${data?.basket_abandonment_rate.toFixed(2)}%`
            : '-'
      }
    ]);
  };

  const getUsersStatsByInterval = async () => {
    const res = await apiService.get(`cs/user/one/${user?.id}/deviations`, {
      params: { interval: getInterval(filters.range) }
    });
    const current_data: ResponseType = res?.data?.current_data;
    const previous_data: ResponseType = res?.data?.previous_data;
    const percentage_deviation: ResponseType = res?.data?.percentage_deviation;

    setUserSummary([
      {
        title: 'total orders',
        value: current_data?.total_orders,
        type:
          Number(previous_data?.total_orders) > Number(current_data?.total_orders)
            ? 'decrease'
            : 'increase',
        weeklyVal: `${Number(percentage_deviation?.total_orders).toFixed(2)}%`
      },
      {
        title: 'total spent',
        value: current_data?.total_amount_spent
          ? formatNumber(current_data?.total_amount_spent, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            })
          : '-',
        type:
          previous_data?.total_amount_spent > current_data?.total_amount_spent
            ? 'decrease'
            : 'increase',
        weeklyVal: `${Number(percentage_deviation?.total_amount_spent).toFixed(2)}%`
      },
      {
        title: 'AVG. ORDER DURATION',
        value: current_data?.average_order_duration
          ? timeConvert(current_data?.average_order_duration)
          : '-',
        type:
          previous_data?.average_order_duration > current_data?.average_order_duration
            ? 'decrease'
            : 'increase',
        weeklyVal: `${Number(percentage_deviation?.average_order_duration).toFixed(2)}%`
      },
      {
        title: 'BASKET ABANDONMENT',
        value: current_data?.basket_abandonment_rate
          ? `${Number(current_data?.basket_abandonment_rate).toFixed(2)}%`
          : '-',
        type:
          previous_data?.basket_abandonment_rate > current_data?.basket_abandonment_rate
            ? 'decrease'
            : 'increase',
        weeklyVal: `${Number(percentage_deviation?.basket_abandonment_rate).toFixed(2)}%`
      }
    ]);
  };
  const loadSummaryData = () => {
    setLoading(true);
    if (filters.range === ENUM_DATE_RANGE.SELECT || ENUM_DATE_RANGE.ALL) {
      getUsersStatsByDate();
    } else {
      getUsersStatsByInterval();
    }
    setLoading(false);
  };

  useEffect(() => {
    loadSummaryData();
  }, [filters]);
  return (
    <div className='space-y-10'>
      <div className=' flex items-center gap-4 flex-wrap'>
        <img
          src={DisplayPicture}
          alt='user display picture'
          className='w-9 h-9 rounded-[50%] flex justify-center items-center'
        />
        <div className='grow'>
          <p className='font-semibold text-2xl capitalize'>{getUserName(user)}</p>
          <span className='text-sm text-gray-5 '>
            Joined {moment(user?.created_at).format('MMM Do, YYYY')}
          </span>
        </div>
        <DateRangeSelector showTime onChange={(v) => setFilters(v)} />
      </div>

      {loading ? (
        <Loader />
      ) : (
        <div className='flex max-md:flex-col gap-2 justify-between bg-[#F5FCF3] items-stretch md:items-start border border-[#DEE1E3] rounded-[8px] px-4 py-4 text-sm mb-4'>
          {userSummary.length === 0 && (
            <div className='leading-[130px] text-center w-full'>
              <p>Loading...</p>
            </div>
          )}
          {userSummary?.map(({ title, type, time, value, weeklyVal, withNaira }, i) => (
            <div
              className={`md:basis-[20%] py-2 flex justify-center items-center gap-4 ${
                userSummary.length - 1 === i
                  ? ''
                  : 'border-b md:border-b-0 md:border-r border-[#DEE1E3]'
              }`}
              key={i}>
              <BaseStats
                title={title}
                type={type}
                weeklyVal={weeklyVal}
                value={value}
                time={time}
                withNaira={withNaira}
                activeDateRange={filters.range}
                small
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserprofileHeader;
